import { z } from "zod"
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ChangeEvent, lazy, Suspense, useEffect, useMemo, useState, type KeyboardEvent } from 'react'
import TabsList from '../../../components/Common/TabsList'
import { FormsSubscriptionOrderBy, FormsSubscriptionService } from "../../../client"
import { userHasPermission } from "../../../utils"
import { NoPermission } from "../../../components/UI/no-permission"
import { useQuery } from "@tanstack/react-query"
import { Box, Center, Flex, SkeletonText, Table, Spinner, useDisclosure } from "@chakra-ui/react"
import SearchField from "../../../components/Common/SearchField"
import Sort from "../../../components/Common/Sort"
import ActionsMenu from "../../../components/Common/ActionsMenu"
import { PaginationFooter } from "../../../components/Common/PaginationFooter"
const DetailSubscription = lazy(() => import("../../../components/Forms/DetailSubscription"))

const subscriptionSearchSchema = z.object({
  page: z.number().catch(1),
})

export const Route = createFileRoute('/_layout/forms/subscriptions')({
  component: Subscriptions,
  validateSearch: (search) => subscriptionSearchSchema.parse(search),
})

const PER_PAGE = 25

function getSubscriptionQueryOptions({
    page,
    order_by,
    descending,
    query,
}: {
    page: number
    order_by?: FormsSubscriptionOrderBy
    descending?: boolean
    query?: string
}) {
    return {
        queryFn: async () => {
            const data = await FormsSubscriptionService.readFormsSubscriptions({
                skip: (page - 1) * PER_PAGE,
                limit: PER_PAGE,
                orderBy: order_by,
                descending: descending,
                query: query,
            })

            return data ?? { data: null, count: 0 }
        },
        queryKey: ["forms-subscriptions", { page, order_by, descending, query }],
    }
}

function SubscriptionTable() {
    // Permission check
    const [su_read, su_delete] = useMemo(() => {
        return ["read", "delete"].map(
            (action) => userHasPermission([{ perm: `form_subscription_${action}`, is_allowed: false }])[0].is_allowed
        )
    }, [userHasPermission])

    if(!su_read) return <NoPermission/>

    const fields = [
        { title: "Typ", col: "type", align: undefined },
        { title: "Titel", col: null, align: undefined },
        { title: "Vorname", col: "firstname", align: undefined },
        { title: "Name", col: "lastname", align: undefined },
        { title: "Adresse", col: null, align: undefined },
        { title: "Ort", col: null, align: undefined },
        { title: "PLZ", col: null, align: undefined },
        { title: "E-Mail", col: "email", align: undefined },
        { title: "Abweichende Rechnungsadresse", col: null, align: undefined },
        { title: "Erstellungsdatum", col: "created_at", align: undefined },
        { title: "ID", col: null, align: undefined },
        { title: "Aktionen", col: null, align: "right" as const },
    ]

    // Page
    const { page } = Route.useSearch()
    const navigate = useNavigate({ from: Route.fullPath })
    const setPage = (page: number) => navigate({
        search: (prev: { [key: string]: string }) => ({ ...prev, page }),
    })

    // Search
    const [inputValue, setInputValue] = useState<string>("")
    const [searchValue, setSearchValue] = useState<string>("")
    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.target.value)
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            if (inputValue != null) {
            setSearchValue(inputValue)
            }
        }
    }

    const handleOnClick = () => {
        if (inputValue != null) {
            setSearchValue(inputValue)
        }
    }

    const handleClear = () => {
        setInputValue("")
        if (inputValue != null) {
            setSearchValue("")
        }
    }

    // Set page on search value change
    const [prevPage, setPrevPage] = useState<number>(page)
    useEffect(() => {
        if(searchValue == "") {
            setPage(prevPage)
        } else {
            setPrevPage(page)
            setPage(1)
        }
    }, [searchValue])

    // Sorting
    const [columnName, setColumnName] = useState<FormsSubscriptionOrderBy>()
    const [orderBy, setOrderBy] = useState<string>("")
    const [isDesc, setIsDesc] = useState<boolean>()

    // Query
    const queryOptions = useMemo(() =>
        getSubscriptionQueryOptions({
          page,
          order_by: columnName,
          descending: isDesc,
          query: searchValue,
        }),
    [page, columnName, isDesc, searchValue])

    const {
        data: subscriptions,
        isPending,
        isPlaceholderData,
    } = useQuery({
        ...queryOptions,
        placeholderData: (prevData) => prevData,
    })

    // Sorting
    const sortTable = (name: FormsSubscriptionOrderBy) => {
        if (columnName !== name) {
            setColumnName(name)
            setOrderBy("")
            sorting("")
        } else {
            sorting(orderBy)
        }
    }

    const sorting = (order: string) => {
        switch (order) {
            case "":
                setOrderBy("desc")
                setIsDesc(true)
            break
            case "desc":
                setOrderBy("asc")
                setIsDesc(false)
            break
            case "asc":
                setOrderBy("")
                setIsDesc(undefined)
                setColumnName(undefined)
            break
        }
    }

    // Open forms-subscription detail view
    const detailModal = useDisclosure()
    const [subscriptionId, setSubscriptionId] = useState("")

    const openModal = (id: string) => {
        setSubscriptionId(id)
        detailModal.onOpen()
    }

    return (
        <>
            <Flex py={8} justify={{base: "flex-start",md: "flex-end"}}>
                <SearchField
                    inputValue={inputValue}
                    onInputChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onClick={handleOnClick}
                    onClear={handleClear}
                />
            </Flex>

            {detailModal.open &&
                <Suspense fallback={
                <Center
                    bg="rgba(0, 0, 0, 0.3)"
                    position="fixed"
                    top="0"
                    left="0"
                    width="100vw"
                    height="100vh"
                    zIndex="10000"
                >
                    <Spinner size="lg" color="white" />
                </Center>}
                >
                <DetailSubscription
                    isOpen={detailModal.open}
                    onClose={detailModal.onClose}
                    subscriptionId={subscriptionId}
                />
                </Suspense>
            }

            <Table.ScrollArea asChild>
                <Table.Root size={{ base: "sm", md: "md" }} whiteSpace="normal" display={{md: "table"}}>
                <Table.Header>
                    <Table.Row>
                    {fields.map((field, i) =>
                        field.col != null ? (
                        <Table.ColumnHeader
                            key={i}
                            onClick={() => sortTable(field.col as FormsSubscriptionOrderBy)}
                            cursor="pointer"
                            textAlign={field.align}
                        >
                            {field.title}
                            {columnName === field.col &&
                            <Sort
                                columnNamePassed={columnName}
                                orderByPassed={orderBy}
                            />
                            }
                        </Table.ColumnHeader>
                        ) : (
                        <Table.ColumnHeader key={field.title} textAlign={field.align}>
                            {field.title}
                        </Table.ColumnHeader>
                        )
                    )}
                    </Table.Row>
                </Table.Header>
                {isPending ? (
                    <Table.Body>
                    <Table.Row>
                        {new Array(12).fill(null).map((_, index) => (
                            <Table.Cell key={index}>
                                <SkeletonText noOfLines={1} paddingBlock="16px" />
                            </Table.Cell>
                        ))}
                    </Table.Row>
                    </Table.Body>
                ) : (
                    <Table.Body>
                    {!subscriptions?.data ? (
                        <Table.Row>
                        <Table.Cell colSpan={12} textAlign="center">
                            Keine Abos verfügbar
                        </Table.Cell>
                        </Table.Row>
                    ) : (
                        subscriptions?.data.map((subscription, i) => (
                        <Table.Row key={i} opacity={isPlaceholderData ? 0.5 : 1}>
                            <Table.Cell onClick={() => openModal(subscription.id)} cursor="pointer">
                                {subscription.type}
                            </Table.Cell>

                            <Table.Cell onClick={() => openModal(subscription.id)} cursor="pointer">
                                {subscription.title}
                            </Table.Cell>

                            <Table.Cell onClick={() => openModal(subscription.id)} cursor="pointer">
                                {subscription.firstname}
                            </Table.Cell>

                            <Table.Cell onClick={() => openModal(subscription.id)} cursor="pointer">
                                {subscription.lastname}
                            </Table.Cell>

                            <Table.Cell onClick={() => openModal(subscription.id)} cursor="pointer">
                                {subscription.address}
                            </Table.Cell>

                            <Table.Cell onClick={() => openModal(subscription.id)} cursor="pointer">
                                {subscription.city}
                            </Table.Cell>

                            <Table.Cell onClick={() => openModal(subscription.id)} cursor="pointer">
                                {subscription.postcode}
                            </Table.Cell>

                            <Table.Cell onClick={() => openModal(subscription.id)} cursor="pointer">
                                {subscription.email}
                            </Table.Cell>

                            <Table.Cell onClick={() => openModal(subscription.id)} cursor="pointer">
                                <Flex gap={2}>
                                    <Box
                                        w="2"
                                        h="2"
                                        borderRadius="50%"
                                        bg={subscription.has_billing_address ? "success" : "danger"}
                                        alignSelf="center"
                                    />
                                    {subscription.has_billing_address ? "Ja" : "Nein"}
                                </Flex>
                            </Table.Cell>

                            <Table.Cell onClick={() => openModal(subscription.id)} cursor="pointer">
                                {new Date(`${subscription.created_at}`).toLocaleString(
                                    "de-CH",
                                    { timeZone: "Europe/Zurich" },
                                )}
                            </Table.Cell>

                            <Table.Cell onClick={() => openModal(subscription.id)} cursor="pointer">
                                {subscription.id}
                            </Table.Cell>

                            <Table.Cell textAlign="right">
                            <ActionsMenu
                                type="Abo"
                                value={subscription}
                                disableDelete={!su_delete}
                                hideEdit={true}
                            />
                            </Table.Cell>
                        </Table.Row>
                        ))
                    )}
                    </Table.Body>
                )}
                </Table.Root>
            </Table.ScrollArea>
            <PaginationFooter
                onChangePage={setPage}
                page={page}
                count={subscriptions?.count ? subscriptions?.count : 0}
                perPage={PER_PAGE}
            />
        </>
    )
}

export default SubscriptionTable

function Subscriptions() {
    const tabs = useMemo(() => [
        { value: "forms", title: "Kontaktanfragen" },
        { value: "forms/subscriptions", title: "Abo-Bestellungen" },
        { value: "forms/newsletters", title: "Newsletter" },
      ], [])

      return <TabsList tabs={tabs} defaultVal="forms" />
}
